import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import CalculateOutlined from '@mui/icons-material/CalculateOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import MapOutlined from '@mui/icons-material/MapOutlined';
import SatelliteAltOutlined from '@mui/icons-material/SatelliteAltOutlined';
import ScaleOutlined from '@mui/icons-material/ScaleOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { CatenaLinkedFleet } from '~hooks/useCatena/models';
import useCatena from '~hooks/useCatena/useCatena';
import { FastWeighConnection } from '~hooks/useFastWeigh/models';
import useFastWeigh from '~hooks/useFastWeigh/useFastWeigh';
import { HaulWareConnection } from '~hooks/useHaulWare/models';
import useHaulWare from '~hooks/useHaulWare/useHaulWare';
import { RutterConnection } from '~hooks/useRutter/models';
import useRutter from '~hooks/useRutter/useRutter';
import { RUTTER_CONNECTIONS_FIRST_PAGE } from '~hooks/useRutter/useRutterConnections';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import StatusChip from '~pages/Settings/components/StatusChip';
import useCommandAlkonConnectionQuery from '~queries/useCommandAlkonConnectionQuery';
import useVetaCenterConnectionQuery from '~queries/useVetaCenterConnectionQuery';
import { ROUTES as routes } from '~router/routePaths';
import { useStores } from '~store';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

type IntegrationCard = {
  icon: ReactNode;
  name: string;
  description: string;
  connected: boolean;
  // When omitted, the Manage button follows `connected`. Catena sets this so the
  // button stays reachable (to invite a first fleet) even before any fleet connects.
  canManage?: boolean;
  link: string;
};

export const IntegrationManagement = observer(() => {
  const { listLinkedFleets } = useCatena();
  const { getConnection } = useHaulWare();
  const { listConnections } = useFastWeigh();
  const { getConnections } = useRutter();
  const [isLoadingCatenaLinkedFleets, setIsLoadingCatenaLinkedFleets] = useState(false);
  const [hasLoadedHaulWareConnection, setHasLoadedHaulWareConnection] = useState(false);
  const [isLoadingFastWeighConnection, setIsLoadingFastWeighConnection] = useState(false);
  const [hasLoadedRutterConnections, setHasLoadedRutterConnections] = useState(false);
  const [rutterConnections, setRutterConnections] = useState<RutterConnection[]>([]);
  const [catenaLinkedFleets, setCatenaLinkedFleets] = useState<CatenaLinkedFleet[]>([]);
  const [haulWareConnection, setHaulWareConnection] = useState<HaulWareConnection | null>(
    null,
  );
  const [fastWeighConnection, setFastWeighConnection] =
    useState<FastWeighConnection | null>(null);
  const { userStore } = useStores();

  const fastWeighFeatureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.fastWeighEnabled,
  });
  const commandAlkonFeatureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.commandAlkonIntegration,
  });
  const { isCommandAlkonConnected, isLoading: isLoadingCommandAlkonConnection } =
    useCommandAlkonConnectionQuery();
  const vetaCenterFeatureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.vetaCenterIntegration,
  });
  const { isLoading: isLoadingVetaCenterConnection, isVetaCenterConnected } =
    useVetaCenterConnectionQuery();
  // One read per render: getPermissions() rebuilds a Set and the whole permissions
  // object on every call.
  const {
    canManageAccountingIntegration: userCanManageAccountingIntegration,
    canManageTelematicsIntegration: userCanManageTelematicsIntegration,
    canManageHaulWareIntegration: userCanManageHaulWareIntegration,
    canManageFastWeighIntegration: userCanManageFastWeighIntegration,
    canManageCommandAlkonIntegration: userCanManageCommandAlkonIntegration,
    canManageVetaCenterIntegration: userCanManageVetaCenterIntegration,
  } = userStore.getPermissions();

  const companyId = getEffectiveUserCompanyId(userStore);

  const isFastWeighCardVisible =
    userCanManageFastWeighIntegration &&
    fastWeighFeatureFlag.isReady &&
    fastWeighFeatureFlag.isFeatureFlagEnabled;

  const isCommandAlkonCardVisible =
    userCanManageCommandAlkonIntegration &&
    commandAlkonFeatureFlag.isReady &&
    commandAlkonFeatureFlag.isFeatureFlagEnabled;

  const isVetaCenterCardVisible =
    userCanManageVetaCenterIntegration &&
    vetaCenterFeatureFlag.isReady &&
    vetaCenterFeatureFlag.isFeatureFlagEnabled;

  const isLoading =
    isLoadingCatenaLinkedFleets ||
    (userCanManageAccountingIntegration && !hasLoadedRutterConnections) ||
    isLoadingFastWeighConnection ||
    (userCanManageHaulWareIntegration && !hasLoadedHaulWareConnection) ||
    (isCommandAlkonCardVisible && isLoadingCommandAlkonConnection) ||
    (isVetaCenterCardVisible && isLoadingVetaCenterConnection);

  useEffect(() => {
    if (!userCanManageAccountingIntegration) return;

    setHasLoadedRutterConnections(false);

    const controller = new AbortController();

    const fetchRutterConnections = async () => {
      try {
        const response = await getConnections(
          RUTTER_CONNECTIONS_FIRST_PAGE,
          controller.signal,
        );
        setRutterConnections(response?.data ?? []);
      } catch (error) {
        if (controller.signal.aborted) return;

        console.error('Failed to fetch Rutter connections:', error);
      } finally {
        if (!controller.signal.aborted) {
          setHasLoadedRutterConnections(true);
        }
      }
    };

    void fetchRutterConnections();

    return () => controller.abort();
  }, [userCanManageAccountingIntegration, companyId, getConnections]);

  useEffect(() => {
    if (!userCanManageTelematicsIntegration) return;

    const fetchCatenaLinkedFleets = async () => {
      setIsLoadingCatenaLinkedFleets(true);
      try {
        const linkedFleets = await listLinkedFleets();
        setCatenaLinkedFleets(linkedFleets?.data ?? []);
      } finally {
        setIsLoadingCatenaLinkedFleets(false);
      }
    };

    void fetchCatenaLinkedFleets();
  }, [userCanManageTelematicsIntegration, listLinkedFleets]);

  useEffect(() => {
    if (!userCanManageHaulWareIntegration) return;

    const fetchHaulWareConnection = async () => {
      try {
        setHaulWareConnection(await getConnection(companyId));
      } catch {
        setHaulWareConnection(null);
      } finally {
        setHasLoadedHaulWareConnection(true);
      }
    };

    void fetchHaulWareConnection();
  }, [userCanManageHaulWareIntegration, companyId, getConnection]);

  useEffect(() => {
    if (!isFastWeighCardVisible) return;

    const fetchFastWeighConnection = async () => {
      setIsLoadingFastWeighConnection(true);
      try {
        const connections = await listConnections();
        setFastWeighConnection(connections?.[0] ?? null);
      } finally {
        setIsLoadingFastWeighConnection(false);
      }
    };

    void fetchFastWeighConnection();
  }, [isFastWeighCardVisible, listConnections]);

  const catenaHasConnectedFleet = useMemo(
    () => catenaLinkedFleets.some((fleet) => fleet.isConnected),
    [catenaLinkedFleets],
  );

  // One connection per company. Only an active connection deep-links into its mapping
  // page; a pending, errored or otherwise non-active one still goes through the list
  // page, where its real status is visible and it can be relinked.
  const linkedRutterConnection = rutterConnections.find(
    (connection) => connection.isConnected,
  );
  const accountingLink = linkedRutterConnection
    ? `${routes.settings.integrations.rutter.base}/${routes.settings.integrations.rutter.quickBooks}?connection_id=${linkedRutterConnection.id}`
    : routes.settings.integrations.rutter.base;

  const integrationCards = useMemo(() => {
    const cards: IntegrationCard[] = [
      {
        icon: <CalculateOutlined />,
        name: t('administration.integration.rutter.name'),
        description: t('administration.integration.rutter.description'),
        connected: userCanManageAccountingIntegration,
        link: accountingLink,
      },
    ];

    if (userCanManageTelematicsIntegration) {
      cards.push({
        icon: <MapOutlined />,
        name: t('administration.integration.catena.name'),
        description: t('administration.integration.catena.description'),
        connected: catenaHasConnectedFleet,
        canManage: true,
        link: routes.settings.integrations.catena.base,
      });
    }

    if (userCanManageHaulWareIntegration) {
      cards.push({
        icon: <LocalShippingOutlined />,
        name: t('administration.integration.haul_ware.name'),
        description: t('administration.integration.haul_ware.description'),
        connected: haulWareConnection?.enabled === true,
        canManage: true,
        link: routes.settings.integrations.haulWare,
      });
    }

    if (isFastWeighCardVisible) {
      cards.push({
        icon: <ScaleOutlined />,
        name: t('administration.integration.fast_weigh.name'),
        description: t('administration.integration.fast_weigh.description'),
        connected: fastWeighConnection?.connected ?? false,
        canManage: true,
        link: routes.settings.integrations.fastWeigh,
      });
    }

    if (isCommandAlkonCardVisible) {
      cards.push({
        icon: <SatelliteAltOutlined />,
        name: t('administration.integration.command_alkon.name'),
        description: t('administration.integration.command_alkon.description'),
        connected: isCommandAlkonConnected,
        canManage: true,
        link: routes.settings.integrations.commandAlkon,
      });
    }

    if (isVetaCenterCardVisible) {
      cards.push({
        icon: <ApartmentOutlined />,
        name: t('administration.integration.veta_center.name'),
        description: t('administration.integration.veta_center.description'),
        connected: isVetaCenterConnected,
        canManage: true,
        link: routes.settings.integrations.vetaCenter,
      });
    }

    return cards;
  }, [
    accountingLink,
    userCanManageAccountingIntegration,
    userCanManageTelematicsIntegration,
    catenaHasConnectedFleet,
    userCanManageHaulWareIntegration,
    haulWareConnection,
    isFastWeighCardVisible,
    fastWeighConnection,
    isCommandAlkonCardVisible,
    isCommandAlkonConnected,
    isVetaCenterCardVisible,
    isVetaCenterConnected,
  ]);

  return (
    <>
      <Box
        sx={{
          maxWidth: '800px',
        }}
      >
        <SettingsPageHeader
          title={t('administration.integration.title')}
          description={t('settings.page_descriptions.integrations')}
        />

        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          integrationCards.map((card) => (
            <Card
              variant="outlined"
              sx={{
                display: 'flex',
                mb: 2,
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
              key={card.name}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography variant="h5">{card.name}</Typography>
                  {card.connected && (
                    <StatusChip
                      isSuccess
                      label={t('administration.integration.connected')}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: 14,
                  }}
                >
                  {card.description}
                </Typography>
              </CardContent>
              <CardActions>
                <List>
                  <SmallButton
                    color="brandV2Yellow"
                    component={Link}
                    disabled={!(card.canManage ?? card.connected)}
                    to={card.link}
                  >
                    {t('administration.integration.manage')}
                  </SmallButton>
                </List>
              </CardActions>
            </Card>
          ))
        )}
      </Box>
    </>
  );
});
